import { useMemo, type MouseEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { resolveLanguage } from '@dorixona/i18n'
import { Logo } from '@dorixona/ui'
import { formatDate } from '@dorixona/utils'
import { openOutside } from '../../app/telegram'
import { BackButton } from '../../ui/BackButton'
import { Icon, IconSprite, type IconName } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { useTextPage, type TextPage } from './api'
import { isPlainText, paragraphs, sanitizeHtml } from './sanitize'
import { CONTACTS, PRIVACY, SECTIONS, type Block, type ContactField } from './privacyPolicy'

/**
 * Политика конфиденциальности — отдельная страница без вкладок: на неё ведут
 * магазины приложений, регистрация и кабинет. Текст одной узкой колонкой по
 * центру — документ читают подряд.
 *
 * Текст пишет админка (`GET /pages/privacy`, HTML её редактора). Пока она его не
 * написала (404), показываем встроенный русский текст: адрес политики стоит в
 * карточках магазинов приложений и пустым быть не может.
 */
export function PrivacyPolicyScreen() {
  const { t, i18n } = useTranslation('common')
  const language = resolveLanguage([i18n.language])
  const page = useTextPage('privacy', language)

  let content: ReactNode
  if (page.isPending) content = <Loading what={t('legal.loading')} shape="lines" count={8} />
  else if (page.isError) content = <Failed error={page.error} onRetry={() => void page.refetch()} />
  else if (page.data) content = <TextPageView page={page.data} language={language} />
  else content = <BuiltInPolicy />

  return (
    <main className="shell shell-bare legal">
      <IconSprite />
      <div className="legal-top">
        <BackButton to="/" />
        <Link to="/" className="legal-logo" aria-label={t('legal.home')}>
          <Logo className="logo-h" />
        </Link>
      </div>
      {content}
    </main>
  )
}

/**
 * Страница из админки. `lang` у документа — язык пришедшего текста: если на
 * языке интерфейса текста нет, сервер отдал русский, и читалка экрана должна
 * произносить его по-русски. Подпись даты — часть приложения, она на языке
 * интерфейса.
 */
function TextPageView({ page, language }: { page: TextPage; language: string }) {
  const { t } = useTranslation('common')
  return (
    <article className="legal-doc" lang={page.lang}>
      <header className="legal-hero">
        <h1>{page.title}</h1>
        {page.updatedAt > 0 ? (
          <dl className="legal-dates" lang={language}>
            <div>
              <dt>{t('legal.updated')}</dt>
              <dd>{formatDate(page.updatedAt)}</dd>
            </div>
          </dl>
        ) : null}
      </header>
      <PageBody body={page.body} />
    </article>
  )
}

/**
 * Текст редактора админки — HTML; вставляем его только после фильтра
 * (sanitize.ts). Текст без единого тега — простой: абзацы по пустым строкам.
 *
 * Чужой сайт открываем, не теряя приложение: в Telegram — его браузером, в
 * нашем Flutter-приложении — через мост, на сайте — новой вкладкой. Ссылки
 * внутри HTML не наши компоненты, поэтому нажатие ловит один обработчик на
 * весь текст. Почта и телефон (`mailto:`, `tel:`) работают обычной ссылкой.
 * Сам контейнер не интерактивный — нажимают ссылки внутри, и с клавиатуры
 * тоже: Enter на ссылке приходит сюда тем же click.
 */
function PageBody({ body }: { body: string }) {
  const plain = isPlainText(body)
  const html = useMemo(() => (plain ? '' : sanitizeHtml(body)), [body, plain])

  if (plain) {
    return (
      <div className="legal-body legal-html legal-plain">
        {paragraphs(body).map((text, i) => (
          <p key={i}>{text}</p>
        ))}
      </div>
    )
  }

  const openLink = (event: MouseEvent<HTMLDivElement>) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]')
    if (!link || !EXTERNAL.test(link.href)) return
    event.preventDefault()
    openOutside(link.href)
  }

  return (
    <div
      className="legal-body legal-html"
      role="presentation"
      onClick={openLink}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

const EXTERNAL = /^https?:\/\//i

/** Встроенный текст: до того как админка напишет страницу. Только по-русски. */
function BuiltInPolicy() {
  return (
    <article className="legal-doc" lang="ru">
      <header className="legal-hero">
        <h1>{PRIVACY.title}</h1>
        <dl className="legal-dates">
          <div>
            <dt>Вступает в силу</dt>
            <dd>{PRIVACY.effective}</dd>
          </div>
          <div>
            <dt>Последнее обновление</dt>
            <dd>{PRIVACY.updated}</dd>
          </div>
        </dl>
        <p className="legal-intro">{PRIVACY.intro}</p>
      </header>

      <div className="legal-body">
        {SECTIONS.map((section, index) => (
          <section key={section.id} id={section.id} aria-labelledby={`${section.id}-h`}>
            <h2 id={`${section.id}-h`}>
              <span className="legal-n" aria-hidden="true">
                {index + 1}
              </span>
              {section.title}
            </h2>
            {section.blocks.map((block, i) => (
              <BlockView key={i} block={block} />
            ))}
          </section>
        ))}
        <p className="legal-foot">{PRIVACY.footer}</p>
      </div>
    </article>
  )
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'p':
      return <p>{block.text}</p>
    case 'list':
      return (
        <ul className="legal-list">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )
    case 'defs':
      return (
        <dl className="legal-defs">
          {block.items.map((item) => (
            <div key={item.term}>
              <dt>{item.term}</dt>
              <dd>{item.text}</dd>
            </div>
          ))}
        </dl>
      )
    case 'note':
      return (
        <aside className="legal-note">
          <Icon name="i-info" size={20} />
          <p>
            <b>{block.title}.</b> {block.text}
          </p>
        </aside>
      )
    case 'contacts':
      return <Contacts fields={block.fields} />
  }
}

const FIELDS: Record<
  ContactField,
  { label: string; icon: IconName; value: string; href?: string }
> = {
  name: { label: 'Наименование', icon: 'i-store', value: CONTACTS.company },
  company: { label: 'Компания', icon: 'i-store', value: CONTACTS.company },
  site: { label: 'Сайт', icon: 'i-home', value: CONTACTS.site, href: CONTACTS.siteUrl },
  email: {
    label: 'Электронная почта',
    icon: 'i-mail',
    value: CONTACTS.email,
    href: `mailto:${CONTACTS.email}`,
  },
  phone: { label: 'Телефон', icon: 'i-phone', value: CONTACTS.phone, href: CONTACTS.phoneHref },
}

/** Реквизиты карточкой: почта, телефон и сайт нажимаются. */
function Contacts({ fields }: { fields: ContactField[] }) {
  return (
    <dl className="legal-contacts">
      {fields.map((field) => {
        const { label, icon, value, href } = FIELDS[field]
        return (
          <div key={field}>
            <span className="legal-contacts-icon" aria-hidden="true">
              <Icon name={icon} size={18} />
            </span>
            <dt>{label}</dt>
            <dd>
              {href ? (
                <a href={href} target={field === 'site' ? '_blank' : undefined} rel="noreferrer">
                  {value}
                </a>
              ) : (
                value
              )}
            </dd>
          </div>
        )
      })}
    </dl>
  )
}
